import { PluginKey } from '@tiptap/pm/state'
import { npubEncode } from '@nostr/tools/nip19'
import { searchPubKeysFromLocal } from '@/services/client.service'
import { getActiveArgIndex } from './suggestionState'
import { findArgsMatch, insertArgValue, resolveActiveDefinition } from './argsFlow'
import { createSuggestionRender } from './slash-popup'
import ArgsList, { TArgsListItem } from './ArgsList'

export type TSlashArgItem = TArgsListItem

type RendererProps = {
  items: TArgsListItem[]
  command: (item: TArgsListItem) => void
}

const argsPluginKey = new PluginKey('slashArgsSuggestion')

const argsSuggestion = {
  char: '\u0000',
  pluginKey: argsPluginKey,
  findSuggestionMatch: findArgsMatch,

  // `query` is the partially typed word before the cursor; it filters the list
  items: ({ query }: { query: string }) => {
    const index = getActiveArgIndex()
    if (index < 0) return []

    const arg = resolveActiveDefinition()?.args[index]
    if (!arg) return []

    const q = query.toLowerCase()
    const matches = (s: string) => !q || s.toLowerCase().includes(q)
    const items: TSlashArgItem[] = []

    if (arg.type === 'enum' && arg.choices.length > 0) {
      for (const choice of arg.choices) {
        if (matches(choice)) items.push({ kind: 'choice', value: choice })
      }
    } else if (arg.type === 'pubkey') {
      for (const pk of searchPubKeysFromLocal(query, 20)) {
        items.push({ kind: 'pubkey', value: npubEncode(pk) })
      }
    } else if (arg.type === 'relay') {
      const relays = (window.hallway.universe?.bigRelayUrls ?? []) as string[]
      for (const url of relays) {
        if (matches(url)) items.push({ kind: 'relay', value: url })
      }
    } else {
      // word/text: the typed text itself is the value; offer it as one entry
      items.push({ kind: 'text', value: query })
    }
    return items
  },

  command: ({
    editor,
    range,
    props
  }: {
    editor: any
    range: { from: number; to: number }
    props: TSlashArgItem
  }) => {
    if (props.kind === 'text' && !props.value) return
    insertArgValue(editor, range, props.value)
  },

  render: createSuggestionRender<RendererProps>(ArgsList)
}

export default argsSuggestion
